"use client";

import { useState } from "react";
import Link from "next/link";
import { BookOpen, CheckCircle2, ClipboardList, Sparkles, ThumbsUp, XCircle } from "lucide-react";
import { type ExamType } from "@/lib/exam/config";
import { type GenericQuestion } from "@/lib/exam/generic-content";

const OPTION_LABELS = ["A", "B", "C", "D", "E"];

interface Props {
  examType: ExamType;
  examName: string;
  questions: readonly GenericQuestion[];
}

type Phase = "intro" | "quiz" | "review";

export function GenericQuiz({ examType, examName, questions }: Props) {
  const [phase, setPhase] = useState<Phase>("intro");
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  const q = questions[current];
  const isAnswered = current in selected;
  const isRevealed = revealed[current];
  const totalAnswered = Object.keys(selected).length;
  const score = Object.entries(selected).filter(
    ([idx, ans]) => ans === questions[Number(idx)].correct_answer
  ).length;

  function handleSelect(key: string) {
    if (isAnswered) return;
    setSelected((prev) => ({ ...prev, [current]: key }));
    setRevealed((prev) => ({ ...prev, [current]: true }));
  }

  function handleNext() {
    if (current < questions.length - 1) {
      setCurrent((c) => c + 1);
    } else {
      setPhase("review");
    }
  }

  function restart() {
    setPhase("intro");
    setCurrent(0);
    setSelected({});
    setRevealed({});
  }

  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm max-w-lg w-full p-8 text-center">
          <div className="mb-4 flex justify-center">
            <ClipboardList className="w-6 h-6 text-gray-400" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{examName} Quiz</h1>
          <p className="text-gray-500 mb-2">
            {questions.length} questions · Instant feedback · With explanations
          </p>
          <p className="text-sm text-gray-400 mb-8">
            Each question shows the correct answer and explanation immediately after you select.
          </p>
          <button
            onClick={() => setPhase("quiz")}
            className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold text-lg hover:bg-indigo-700 transition-colors"
          >
            Start Quiz →
          </button>
          <Link
            href={`/${examType}/sample-questions`}
            className="block mt-4 text-sm text-gray-400 hover:text-gray-600"
          >
            View questions with answers (no quiz mode)
          </Link>
        </div>
      </div>
    );
  }

  if (phase === "review") {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-10">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center mb-6">
            <div className="mb-4 flex justify-center">
              {pct >= 80 ? (
                <Sparkles className="w-6 h-6 text-indigo-600" strokeWidth={1.75} aria-hidden="true" />
              ) : pct >= 60 ? (
                <ThumbsUp className="w-6 h-6 text-green-600" strokeWidth={1.75} aria-hidden="true" />
              ) : (
                <BookOpen className="w-6 h-6 text-gray-400" strokeWidth={1.75} aria-hidden="true" />
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Quiz Complete</h2>
            <p className="text-gray-500 mb-4">{examName}</p>
            <div className="text-5xl font-bold text-indigo-600 mb-2">
              {score}/{questions.length}
            </div>
            <p className="text-gray-600 mb-6">{pct}% correct</p>
            <div className="flex gap-3">
              <button
                onClick={restart}
                className="flex-1 bg-gray-100 text-gray-800 py-2.5 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Retake Quiz
              </button>
              <Link
                href={`/${examType}`}
                className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl font-semibold hover:bg-indigo-700 transition-colors text-center"
              >
                Back to Hub
              </Link>
            </div>
          </div>

          {/* Per-question review */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Question Review</h3>
          <div className="space-y-4">
            {questions.map((question, idx) => {
              const userAns = selected[idx];
              const correct = question.correct_answer;
              const isRight = userAns === correct;
              return (
                <div
                  key={question.id}
                  className={`rounded-xl border p-4 ${isRight ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    {isRight ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
                    )}
                    <p className="text-sm font-medium text-gray-800 leading-relaxed line-clamp-2">
                      {question.prompt}
                    </p>
                  </div>
                  {!isRight && (
                    <p className="text-xs text-gray-600 ml-8">
                      Your answer: <strong className="text-red-700">{userAns}</strong> · Correct: <strong className="text-green-700">{correct}</strong>
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1 ml-8">{question.explanation}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Quiz phase
  const userSelected = selected[current];
  const isCorrect = userSelected === q.correct_answer;

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-xl mx-auto">
        {/* Progress */}
        <div className="flex items-center justify-between mb-6">
          <Link href={`/${examType}`} className="text-sm text-gray-400 hover:text-gray-600">
            ← {examName}
          </Link>
          <span className="text-sm font-medium text-gray-600">
            {current + 1} / {questions.length}
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-1.5 mb-8">
          <div
            className="bg-indigo-500 h-1.5 rounded-full transition-all"
            style={{ width: `${((current + 1) / questions.length) * 100}%` }}
          />
        </div>

        {/* Question card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
          {q.skill && (
            <span className="inline-block bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full mb-3">
              {q.skill}
            </span>
          )}
          <p className="text-gray-900 font-medium text-base leading-relaxed whitespace-pre-line mb-6">
            {q.prompt}
          </p>

          <div className="space-y-2.5">
            {q.options.map((opt, oi) => {
              let cls = "flex gap-3 w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors ";
              if (!isAnswered) {
                cls += "border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 cursor-pointer";
              } else if (opt.key === q.correct_answer) {
                cls += "border-green-300 bg-green-50 text-green-800";
              } else if (opt.key === userSelected) {
                cls += "border-red-300 bg-red-50 text-red-700";
              } else {
                cls += "border-gray-200 text-gray-400 cursor-default";
              }

              return (
                <button
                  key={opt.key}
                  className={cls}
                  onClick={() => handleSelect(opt.key)}
                  disabled={isAnswered}
                >
                  <span className="font-bold flex-shrink-0 w-5">
                    {OPTION_LABELS[oi]}.
                  </span>
                  <span className="flex-1">{opt.text}</span>
                  {isAnswered && opt.key === q.correct_answer && (
                    <span className="text-green-600 flex-shrink-0">✓</span>
                  )}
                  {isAnswered && opt.key === userSelected && opt.key !== q.correct_answer && (
                    <span className="text-red-500 flex-shrink-0">✗</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Explanation */}
        {isAnswered && (
          <div
            className={`rounded-xl border px-4 py-3 mb-4 ${isCorrect ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
          >
            <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isCorrect ? "text-green-600" : "text-red-600"}`}>
              {isCorrect ? "Correct!" : "Incorrect"}
            </p>
            <p className={`text-sm leading-relaxed ${isCorrect ? "text-green-900" : "text-red-900"}`}>
              {q.explanation}
            </p>
          </div>
        )}

        {isAnswered && (
          <button
            onClick={handleNext}
            className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
          >
            {current < questions.length - 1 ? "Next Question →" : "See Results →"}
          </button>
        )}
      </div>
    </div>
  );
}
